import React from 'react';
import { useTranslation } from 'react-i18next';
import { Square } from 'lucide-react';
import styles from './TutorChat.module.css';
import type { ToolCall } from '../../services/teachingAgent';

const KNOWN_TOOLS = new Set([
  'ask_socratic_question',
  'show_video',
  'show_book_excerpt',
  'celebrate',
  'provide_hint',
  'assess_understanding',
  'demonstrate_code',
  'run_code',
  'explain_concept',
]);

/** Human wording for what Ako is doing right now, or null when it is just writing. */
export function useTutorStatusText(
  active: boolean,
  hasContent: boolean,
  currentTool?: ToolCall | null,
): string | null {
  const { t } = useTranslation();
  if (!active) return null;
  if (currentTool && currentTool.status !== 'completed') {
    return KNOWN_TOOLS.has(currentTool.name)
      ? t(`tutor.status.${currentTool.name}`)
      : t('tutor.status.generic');
  }
  return hasContent ? null : t('tutor.status.thinking');
}

interface Props {
  /** True only for the latest assistant message while a reply is in flight. */
  active: boolean;
  hasContent: boolean;
  currentTool?: ToolCall | null;
  onStop?: () => void;
}

/** Shared status line + Stop used by every portal/reader/shell tutor surface. */
const TutorStatusLine: React.FC<Props> = ({ active, hasContent, currentTool, onStop }) => {
  const { t } = useTranslation();
  const status = useTutorStatusText(active, hasContent, currentTool);
  // Stop must stay reachable while text is streaming, not only before it.
  if (!status && !(active && onStop)) return null;
  return (
    <div className={styles.statusLine}>
      <span className={styles.pulse} aria-hidden />
      <span role="status">{status}</span>
      {onStop && (
        <button type="button" className={styles.stopBtn} onClick={onStop}>
          <Square size={11} aria-hidden />
          {t('tutor.stop')}
        </button>
      )}
    </div>
  );
};

export default TutorStatusLine;
